import { Button, Flex, Grid, useBreakpointValue, useToast } from "@chakra-ui/react";
import * as React from "react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { showErrorToast } from "../../../../../../utils";
import { CategoryGridHeaderRow, CategoryGridItems } from "./index";
import { Paginator } from "../../../../../../components/Paginator";
import { H2 } from "components/text/headings";

function CategoryGrid({
  questionnaireId,
  columnHeaders,
  attributesOrder,
  prepareSectionValues,
  handleAddItem,
  handleEditItem,
  handleDeleteItem,
  getItemsFunction,
  isPreview,
  maxHeight = "220",
  showNoDataLabel = false,
}) {
  const { t } = useTranslation("translation");
  const toast = useToast();

  const addNewItemBtnLabel = useBreakpointValue({
    base: `+ ${t("buttons.add")}`,
    md: `+ ${t("buttons.addNewItems")}`,
  });

  const [itemsList, setItemsList] = useState([]);
  const [paginatorObj, setPaginatorObj] = useState({
    queryPageIndex: 0,
    totalPages: 0,
  });
  const queryPageSize = 10;

  getItemsFunction(
    {
      queryParams: {
        page: paginatorObj.queryPageIndex,
        size: queryPageSize,
        sort: "id,desc",
      },
      questionnaireId: questionnaireId,
    },
    {
      onSuccess: (page) => {
        const pageContent = page?.content ?? [];
        const items = prepareSectionValues(pageContent);
        setItemsList(items);
        if (
          pageContent.length === 0 &&
          page?.number !== 0 &&
          page?.totalElements !== 0 &&
          paginatorObj.queryPageIndex > 0
        ) {
          setPaginatorObj({
            queryPageIndex: paginatorObj.queryPageIndex - 1,
            totalPages: page?.totalPages ?? 0,
          });
        } else {
          setPaginatorObj({
            queryPageIndex: page?.number ?? 0,
            totalPages: page?.totalPages ?? 0,
          });
        }
      },
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0,
    }
  );

  const handlePageSelection = (newPage) => {
    setPaginatorObj({
      ...paginatorObj,
      queryPageIndex: newPage - 1,
    });
  };

  return (
    <Fragment>
      <Flex mb={5} gap={"25%"} overflow="auto" justifyContent={isPreview ? "center" : "start"}>
        {!isPreview ? (
          <Button
            colorScheme="white"
            color="esg_lightgreen"
            border={"1px solid"}
            borderColor={"esg_lightgreen"}
            _focus={{ boxShadow: "none" }}
            minWidth={16}
            onClick={handleAddItem}
          >
            {addNewItemBtnLabel}
          </Button>
        ) : null}
        {itemsList.length > 0 && (
          <Paginator
            selectedPage={paginatorObj.queryPageIndex + 1}
            numberOfPages={paginatorObj.totalPages}
            handlePageSelection={(newPage) => handlePageSelection(newPage)}
          />
        )}
      </Flex>
      {/* Only the action column (if available) has colSpan = 1, and all other columns have colSpan = 2 */}
      {itemsList.length > 0 && (
        <Grid
          templateColumns={`repeat(${attributesOrder.length * 2 + (!isPreview ? 1 : 0)}, 1fr)`}
          minWidth="700"
          maxHeight={maxHeight}
          overflow="auto"
        >
          <CategoryGridHeaderRow
            isPreview={isPreview}
            columnHeaders={!isPreview ? [...columnHeaders, ""] : columnHeaders}
          />
          <CategoryGridItems
            items={itemsList}
            attributesOrder={attributesOrder}
            handleEditItem={handleEditItem}
            handleDeleteItem={handleDeleteItem}
            isPreview={isPreview}
          />
        </Grid>
      )}
      {itemsList.length === 0 && showNoDataLabel && <H2>{t("noDataAvailable")}</H2>}
    </Fragment>
  );
}

export default CategoryGrid;
